import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createOrganization,
  createOrganizationProject,
} from '../../api';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { buildWorkspacePath } from '../../utils/routes';

function OnboardingPage() {
  const { session, signOut } = useSession();
  const {
    workspace,
    activeOrganization,
    setActiveOrgId,
    setActiveProjectId,
    setWorkspaceVersion,
  } = useWorkspace();
  const navigate = useNavigate();

  const organizations = workspace.organizations;

  const [onboardingOrganizationName, setOnboardingOrganizationName] = useState('');
  const [onboardingProjectName, setOnboardingProjectName] = useState('');
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');

  const needsOrg = organizations.length === 0;
  const totalSteps = needsOrg ? 2 : 1;
  const step = Math.min(onboardingStep, totalSteps - 1);
  const isLastStep = step === totalSteps - 1;
  const isOrgStep = needsOrg && step === 0;
  const isProjectStep = !isOrgStep;
  const stepLabels = needsOrg ? ['Workspace', 'Project'] : ['Project'];

  const heading = isOrgStep ? 'Name your organization' : 'Name your project';
  const subheading = isOrgStep
    ? 'This is what your team will see across the workspace.'
    : 'Environments and release settings will live under this project.';

  const currentName = isOrgStep ? onboardingOrganizationName : onboardingProjectName;
  const canAdvance = currentName.trim().length >= 2;

  async function handleOnboardingSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!session) {
      return;
    }

    if (onboardingStep < totalSteps - 1) {
      if (needsOrg && onboardingOrganizationName.trim().length < 2) {
        setAuthError('Workspace name must be at least 2 characters.');
        return;
      }
      setAuthError('');
      setOnboardingStep((current) => current + 1);
      return;
    }

    if (onboardingProjectName.trim().length < 2) {
      setAuthError('Project name must be at least 2 characters.');
      return;
    }

    setAuthBusy(true);
    setAuthError('');

    try {
      const createdOrganization = needsOrg
        ? await createOrganization(session.accessToken, {
            name: onboardingOrganizationName.trim()
          })
        : null;

      const selectedOrganization = createdOrganization
        ?? (needsOrg ? null : (activeOrganization ?? organizations[0]));

      if (!selectedOrganization) {
        throw new Error('No organization available for onboarding');
      }

      const organizationId = createdOrganization
        ? createdOrganization.tenantOrganizationId
        : (selectedOrganization as { id: string }).id || (selectedOrganization as { tenantOrganizationId: string }).tenantOrganizationId;
      const organizationSlug = createdOrganization
        ? createdOrganization.slug
        : (selectedOrganization as { slug: string }).slug;

      const app = await createOrganizationProject(
        session.accessToken,
        organizationId,
        {
          name: onboardingProjectName.trim(),
          kind: 'release',
          enabledProducts: ['ota']
        }
      );

      setActiveOrgId(organizationId);
      setActiveProjectId(app.tenantAppId);
      setWorkspaceVersion((value) => value + 1);
      setOnboardingStep(0);
      navigate(buildWorkspacePath(organizationSlug, app.slug, 'overview'), {
        replace: true
      });
    } catch {
      setWorkspaceVersion((value) => value + 1);
      setAuthError('Failed to create your workspace. Please try again.');
    } finally {
      setAuthBusy(false);
    }
  }

  return (
    <div className="obShell">
      <form className="obCard" onSubmit={handleOnboardingSubmit}>
        <div className="obMark">C</div>

        {totalSteps > 1 ? (
          <div className="obTrack">
            {stepLabels.map((label, i) => (
              <div
                key={label}
                className={`obTrackStep${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`}
              >
                <span className="obTrackDot" />
                <span className="obTrackLabel">{label}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="obBody">
          <div className="obHeading">
            <h1>{heading}</h1>
            <p>{subheading}</p>
          </div>

          {isOrgStep ? (
            <label className="obField">
              <span>Workspace name</span>
              <input
                value={onboardingOrganizationName}
                onChange={(e) => {
                  setOnboardingOrganizationName(e.target.value);
                  setAuthError('');
                }}
                placeholder="Acme Inc."
                maxLength={160}
                autoFocus
              />
            </label>
          ) : null}

          {isProjectStep ? (
            <label className="obField">
              <span>Project name</span>
              <input
                value={onboardingProjectName}
                onChange={(e) => {
                  setOnboardingProjectName(e.target.value);
                  setAuthError('');
                }}
                placeholder="Acme Platform"
                maxLength={160}
                autoFocus
              />
            </label>
          ) : null}

          {authError ? <div className="authError">{authError}</div> : null}
        </div>

        <div className="obFooter">
          <button
            type="button"
            className="secondaryButton"
            onClick={
              step === 0
                ? signOut
                : () => {
                    setAuthError('');
                    setOnboardingStep((v) => Math.max(0, v - 1));
                  }
            }
          >
            {step === 0 ? 'Sign out' : 'Back'}
          </button>
          <button
            type="submit"
            className="primaryButton"
            disabled={authBusy || !canAdvance}
          >
            {authBusy ? 'Creating…' : isLastStep ? 'Create workspace' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { OnboardingPage };
